"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useReactToPrint } from "react-to-print";
import ThermalReceipt from "./ThermalReceipt";
import RefundThermalReceipt from "./RefundThermalReceipt";
import { getReceiptPrintData as apiGetReceiptPrintData } from "../../../api/frontShop/ReceiptAPI";
import styles from "../css/PrintRecieptDialog.module.css";

export default function PrintReceiptDialog({ recieptno, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [copyStage, setCopyStage] = useState("ORIGINAL");

  const printWrapRef = useRef(null);
  const inFlightRef = useRef(false);
  const printedRef = useRef(false);
  const printingStageRef = useRef("ORIGINAL");

  const triggerPrint = useReactToPrint({
    contentRef: printWrapRef,
    onAfterPrint: () => {
      const stage = printingStageRef.current;
      if (stage === "ORIGINAL") {
        printingStageRef.current = "DUPLICATE";
        setCopyStage("DUPLICATE");
        setTimeout(() => {
          if (printWrapRef.current) triggerPrint();
        }, 250);
        return;
      }
      if (onClose) onClose();
    },
    removeAfterPrint: true,
  });

  const handlePrintClick = () => {
    if (loading) return;
    if (error) return;
    if (!data) return;
    if (!printWrapRef.current) return;

    printedRef.current = true;
    printingStageRef.current = "ORIGINAL";
    setCopyStage("ORIGINAL");
    triggerPrint();
  };

  useEffect(() => {
    let cancelled = false;

    if (!open) {
      inFlightRef.current = false;
      printedRef.current = false;
      printingStageRef.current = "ORIGINAL";
      setCopyStage("ORIGINAL");
      setLoading(false);
      setError("");
      setData(null);
      return;
    }

    if (!recieptno) return;
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    setLoading(true);
    setError("");
    setData(null);

    (async () => {
      try {
        const { data: json } = await apiGetReceiptPrintData(recieptno);
        const d = json?.data || null;
        if (cancelled) return;
        setData(d);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to prepare print");
      } finally {
        if (!cancelled) setLoading(false);
        inFlightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, recieptno]);

  useEffect(() => {
    if (!open) return;
    printedRef.current = false;
  }, [open, recieptno]);

  if (!open) return null;

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  const ui = (
    <div>
      <div role="dialog" aria-modal="true" className={`${styles.overlay} ${styles.noPrint}`}>
        <div className={styles.card}>
          <div className={styles.title}>Printing receipt</div>
          <div className={styles.status}>
            {loading ? "Preparing print..." : error ? "Could not prepare print." : "Printing..."}
          </div>

          <div className={styles.progressTrack}>
            <div
              className={[
                styles.progressBar,
                loading ? styles.progressBarLoading : "",
                error ? styles.progressBarError : styles.progressBarOk,
              ]
                .filter(Boolean)
                .join(" ")}
            />
          </div>

          {error ? <div className={styles.errorText}>{error}</div> : null}

          <div className={styles.actions}>
            <button onClick={handlePrintClick} className={styles.cancelBtn} disabled={loading || !!error || !data}>
              Print
            </button>
            <button onClick={() => (onClose ? onClose() : null)} className={styles.cancelBtn}>
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div className={styles.printOnly}>
        <div ref={printWrapRef} className={styles.printRoot}>
          {data?.refund?.isRefunded ? (
            <RefundThermalReceipt data={data} copyLabel={copyStage} />
          ) : (
            <ThermalReceipt data={data} copyLabel={copyStage} />
          )}
        </div>
      </div>
    </div>
  );

  return portalTarget ? createPortal(ui, portalTarget) : ui;
}
